import { useDispatch, useSelector } from 'react-redux';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal, Box } from '@mui/material';
import { KTSVG } from '../../../../_metronic/helpers';
import { CgPathDivide } from 'react-icons/cg';
import { MuiTooltip } from '../../../common/Tooltip';
import InfoWrapper from '../../../modules/common/SectionInfo';
import { iconListSelector } from '../../../reducers/iconReducer';
import React from 'react';
import { updateCollectionAction, collectionLoadingStatusSelector } from '../../../reducers/collectionReducer';

interface SubmissionData {
  id?: number;
  name: string;
  icon: string;
  isAdmin: number;
  isActive: number;
}

interface UpdateModalProps {
  open: boolean;
  onClose: () => void;
  data: SubmissionData;
  onSubmit: (data: SubmissionData) => void;
  selectedRow: any;
}

export const useCollectionTableConfig = () => {
  const dispatch: any = useDispatch();
  const loading = useSelector(collectionLoadingStatusSelector);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [submissionData, setSubmissionData] = useState<any>({});
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [activeLoadingRowId, setActiveLoadingRowId] = useState<number | null>(null);

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'white',
    boxShadow: 24,
    p: 4,
    height: 700,
    overflow: 'scroll',
  };

  const iconModalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'white',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    height: 600,
    overflow: 'scroll',
  };

  const handleOpenUpdateModal = (row: any) => {
    setSelectedRow(row);
    setSubmissionData({
      ...row,
      name: row?.name || '',
      icon: row?.icon || '',
      isAdmin: row?.isAdmin || 0,
    });
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    setSelectedRow(null);
    setSubmissionData({});
    setIsUpdateModalOpen(false);
  };

  const handleSubmit = async (data: SubmissionData) => {
    if (selectedRow) {
      const result = await dispatch(updateCollectionAction({ data, id: selectedRow.id }));
      if (updateCollectionAction.fulfilled.match(result) && result.payload?.success) {
        handleCloseUpdateModal();
      }
    }
  };

  const columns = [
    {
      id: 'index',
      label: '#',
      align: 'left' as const,
      render: (_: any, index: number, currentPage: number, entriesPerPage: number) => (
        <span>{(currentPage - 1) * entriesPerPage + index + 1}</span>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      align: 'left' as const,
      render: (row: any) => (
        <div className='d-flex align-items-center' style={{ maxWidth: '300px', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
          <img
            src={row?.icon}
            alt='icon'
            style={{
              width: 30,
              height: 30,
              marginRight: '10px',
              flexShrink: 0,
            }}
          />
          <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{row?.name || '—'}</span>
        </div>
      ),
    },
    {
      id: 'icon',
      label: 'Icon',
      align: 'left' as const,
      render: (row: any) => (
        <div className='d-flex align-items-center' style={{ maxWidth: '200px' }}>
          <div className='text-truncate' style={{ flex: 1 }}>{row?.icon || '—'}</div>
        </div>
      )
    },
    {
      id: 'isAdmin',
      label: 'isAdmin',
      align: 'left' as const,
      render: (row: any) => (
        <div className='form-check form-check form-switch fv-row'>
          <input
            checked={row?.isAdmin === 1}
            // onChange={(e) => {
            //   if (row?.isAdmin === 1) return;
            //   const finalData = {
            //     ...row,
            //     isAdmin: e.target.checked ? 1 : 0,
            //   };
            //   dispatch(updateCollectionAction({ data: finalData, id: row.id }));
            // }}
            disabled={true}
            className={`form-check-input w-40px h-25px`}
            type='checkbox'
            id='isAdmin'
          />
          <label className='form-check-label'></label>
        </div>
      ),
    },
    {
      id: 'isActive',
      label: 'isActive',
      align: 'left' as const,
      render: (row: any) => (
        <div className='form-check form-check form-switch fv-row'>
          <input
            checked={row?.isActive === 1}
            onChange={(e) => {
              setActiveLoadingRowId(row.id);
              const finalData = {
                ...row,
                isActive: e.target.checked ? 1 : 0,
              };
              dispatch(updateCollectionAction({ data: finalData, id: row.id }));
            }}
            className={`form-check-input w-40px h-25px`}
            type='checkbox'
            disabled={loading === 'loading' && row.id === activeLoadingRowId}
            id='isActive'
          />
          <label className='form-check-label'></label>
        </div>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'center' as const,
      render: (row: any) => (
        <div className='d-flex justify-content-center align-items-center gap-2'>
          <button
            onClick={() => handleOpenUpdateModal(row)}
            type='button'
            className='btn btn-sm btn-primary'
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  const getTableData = (data: any[], currentPage: number, entriesPerPage: number) => {
    if (!data || !Array.isArray(data)) {
      return {
        table: { columns: [], rows: [] },
        pagination: { total: 0, lastPage: 0, currentPage: 1, entriesPerPage: 10 }
      };
    }

    const validCurrentPage = Math.max(1, Math.floor(currentPage || 1));
    const validEntriesPerPage = Math.max(1, Math.floor(entriesPerPage || 10));

    const total = data.length;
    const lastPage = Math.max(1, Math.ceil(total / validEntriesPerPage));
    const startIndex = (validCurrentPage - 1) * validEntriesPerPage;
    const endIndex = Math.min(startIndex + validEntriesPerPage, total);

    const slicedData = data.slice(startIndex, endIndex);

    const rows = slicedData.map((row: any, index: number) => {
      const newRow: any = {};
      columns.forEach((col) => {
        if (col.render) {
          newRow[col.id] = col.render(row, index, validCurrentPage, validEntriesPerPage);
        } else {
          newRow[col.id] = row[col.id];
        }
      });
      return newRow;
    });

    return {
      table: {
        columns,
        rows
      },
      pagination: {
        total,
        lastPage,
        currentPage: validCurrentPage,
        entriesPerPage: validEntriesPerPage
      }
    };
  };

  const UpdateModal: React.FC<UpdateModalProps> = ({
    open,
    onClose,
    data,
    onSubmit,
    selectedRow
  }) => {
    const [formData, setFormData] = useState<SubmissionData>(data);
    const icons = useSelector(iconListSelector);
    const [libraryIcon, setLibraryIcon] = useState('');
    const [isIconModalOpen, setIsIconModalOpen] = useState(false);

    useEffect(() => {
      setFormData(data);
    }, [data]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        name: e.target.value,
      }));
    }, []);

    const handleIconChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        icon: e.target.value,
      }));
    }, []);

    const handleIsAdminChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedRow?.isAdmin === 1) return;
      setFormData((prev: SubmissionData) => ({
        ...prev,
        isAdmin: e.target.checked ? 1 : 0,
      }));
    }, [selectedRow?.isAdmin]);

    const handleIsActiveChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        isActive: e.target.checked ? 1 : 0,
      }));
    }, []);

    const handleFormSubmit = useCallback((e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    }, [onSubmit, formData]);

    const handleOpenIconModal = useCallback(() => {
      setIsIconModalOpen(true);
    }, []);

    const handleCloseIconModal = useCallback(() => {
      setIsIconModalOpen(false);
      setLibraryIcon('');
    }, []);

    const handleIconSelect = useCallback((icon: string) => {
      setFormData((prev: SubmissionData) => ({
        ...prev,
        icon,
      }));
      handleCloseIconModal();
    }, [handleCloseIconModal]);

    const formContent = useMemo(() => (
      <form className='text-center m-5' onSubmit={handleFormSubmit}>
        <InfoWrapper bar={'down'}>
          <div>
            <div className='row mb-4 text-start'>
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6'>
                  <span className='required'>Name</span>
                </label>
                <div className='fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Collection Name'
                    value={formData?.name || ''}
                    onChange={handleNameChange}
                  />
                </div>
              </div>
              <div className='col-lg-6'>
                <label className='col-form-label fw-bold fs-6'>
                  <span className='required'>Icon</span>
                </label>
                <div className='d-flex'>
                  <input
                    type='text'
                    value={formData?.icon || ''}
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Icon Link'
                    onChange={handleIconChange}
                    disabled={true}
                  />
                  <MuiTooltip title='Add Icon from Library'>
                    <div className='col-lg-1 text-center m-auto' onClick={handleOpenIconModal}>
                      <CgPathDivide className='ms-3 pointer' size={'2rem'} />
                    </div>
                  </MuiTooltip>
                </div>
              </div>
            </div>
          </div>
        </InfoWrapper>
        <div className='row'>
          <div className='mb-4 col-6'>
            <div className='d-flex justify-content-between'>
              <label className='col-form-label fw-bold fs-6'>Active</label>
              <div className='d-flex align-items-center'>
                <div className='form-check form-check form-switch fv-row'>
                  <input
                    checked={formData?.isActive === 1}
                    onChange={handleIsActiveChange}
                    className='form-check-input w-40px h-25px'
                    type='checkbox'
                    id='isActive'
                  />
                  <label className='form-check-label'></label>
                </div>
              </div>
            </div>
          </div>
          {/* <div className='mb-4 col-6'>
            <div className='d-flex justify-content-between'>
              <label className='col-form-label fw-bold fs-6'>Admin</label>
              <div className='d-flex align-items-center'>
                <div className='form-check form-check form-switch fv-row'>
                  <input
                    checked={formData?.isAdmin === 1}
                    onChange={handleIsAdminChange}
                    disabled={selectedRow?.isAdmin === 1}
                    className='form-check-input w-40px h-25px'
                    type='checkbox'
                    id='isAdmin'
                  />
                  <label className='form-check-label'></label>
                </div>
              </div>
            </div>
          </div> */}
        </div>
        <div className='d-flex justify-content-center mt-20'>
          <button
            type='submit'
            className='btn btn-primary mt-4'
            aria-label='submit data'
          >
            Submit
          </button>
        </div>
      </form>
    ), [
      formData,
      selectedRow,
      handleNameChange,
      handleIconChange,
      handleIsAdminChange,
      handleIsActiveChange,
      handleFormSubmit,
      handleOpenIconModal
    ]);

    return (
      <>
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box className='overflow-scroll' sx={style}>
            <div className='modal-header'>
              <h2 className='fw-bolder'>Update Collection</h2>
              <div
                onClick={onClose}
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{ cursor: 'pointer' }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>
            {formContent}
          </Box>
        </Modal>

        <Modal
          open={isIconModalOpen}
          onClose={handleCloseIconModal}
          aria-labelledby="icon-modal-title"
          aria-describedby="icon-modal-description"
        >
          <Box sx={iconModalStyle}>
            <div className='modal-header mb-2'>
              <h2 className='fw-bolder'>Icons</h2>
              <div
                onClick={handleCloseIconModal}
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{ cursor: 'pointer' }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>
            <div className='card-title text-center'>
              <div className='d-flex align-items-center my-3 m-auto'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-1 position-absolute ms-6'
                />
                <input
                  onChange={(e) => setLibraryIcon(e.target.value)}
                  type='text'
                  data-kt-user-table-filter='search'
                  className='form-control form-control-solid w-250px ps-14'
                  placeholder='Search icons'
                />
              </div>
            </div>
            <div className='row mt-4'>
              {libraryIcon
                ? icons
                  ?.filter((f) => f?.name?.toUpperCase().includes(libraryIcon.toUpperCase()))
                  ?.map((icon: any, index: any) => (
                    <div className='col-3 rounded text-center my-1' key={index}>
                      <div
                        style={{ width: '100px', height: '50px' }}
                        className='symbol symbol-35px text-center bg-secondary align-items-center pointer'
                        onClick={() => handleIconSelect(icon.icon)}
                        data-bs-toggle='tooltip'
                        title={icon.name}
                      >
                        <p className='fw-bold fs-4 text-primary mb-0 mt-4'>
                          <img src={icon.icon} alt='icon' width={30} height={30} />
                        </p>
                      </div>
                      <div className='fs-7'>{icon.name}</div>
                    </div>
                  ))
                : icons?.map((icon: any, index: any) => (
                  <div className='col-3 rounded text-center my-1' key={index}>
                    <div
                      style={{ width: '100px', height: '50px' }}
                      className='symbol symbol-35px text-center bg-secondary align-items-center pointer'
                      onClick={() => handleIconSelect(icon.icon)}
                      data-bs-toggle='tooltip'
                      title={icon.name}
                    >
                      <p className='fw-bold fs-4 text-primary mb-0 mt-4'>
                        <img src={icon.icon} alt='icon' width={30} height={30} />
                      </p>
                    </div>
                    <div className='fs-7'>{icon.name}</div>
                  </div>
                ))}
            </div>
          </Box>
        </Modal>
      </>
    );
  };

  useEffect(() => {
    if (loading !== 'loading') {
      setActiveLoadingRowId(null);
    }
  }, [loading]);

  return {
    columns,
    getTableData,
    Modals: () => (
      <UpdateModal
        open={isUpdateModalOpen}
        onClose={handleCloseUpdateModal}
        data={submissionData}
        onSubmit={handleSubmit}
        selectedRow={selectedRow}
      />
    )
  };
};

